import { Bell, Phone } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { getLeadFollowupSummary } from "../../api/api";
import { useAuth } from "../../context/AuthContext";
import { adminPath } from "../../config/adminRouting";
import { canUseModule } from "../../config/permissions";

export function TopBarActions({ children }) {
  const [target, setTarget] = useState(null);
  useEffect(() => {
    setTarget(document.getElementById("admin-topbar-actions"));
  }, []);
  return target ? createPortal(children, target) : null;
}

export default function TopBar({ pageContext = null, onNavigate = null }) {
  const { user, logout } = useAuth();
  const [followups, setFollowups] = useState({ totalDue: 0, items: [] });
  const [open, setOpen] = useState(false);
  const panelRef = useRef(null);

  const getCurrentDate = () => {
    const options = { weekday: "short", month: "short", day: "numeric" };
    return new Date().toLocaleDateString("en-US", options);
  };

  const handleLogout = () => {
    logout();
  };

  // lead follow-ups are a vendor's own data: only an account that may open Leads asks for them (never the platform account)
  const showFollowups = canUseModule(user, "leads");

  useEffect(() => {
    if (!showFollowups) {
      setFollowups({ totalDue: 0, items: [] });
      return undefined;
    }
    let active = true;
    const load = async () => {
      try {
        const res = await getLeadFollowupSummary();
        if (active) setFollowups(res.data || { totalDue: 0, items: [] });
      } catch {
        if (active) setFollowups({ totalDue: 0, items: [] });
      }
    };
    load();
    const timer = setInterval(load, 60000);
    return () => {
      active = false;
      clearInterval(timer);
    };
  }, [showFollowups]);

  useEffect(() => {
    const close = (event) => {
      if (panelRef.current && !panelRef.current.contains(event.target)) setOpen(false);
    };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, []);

  const openLeads = () => {
    setOpen(false);
    if (typeof onNavigate === "function") onNavigate("leads");
  };

  return (
    <header className="dashboard-topbar">
      <div className="topbar-left">
        <div className="user-profile">
          <div className="user-avatar">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
              <circle cx="12" cy="7" r="4"></circle>
            </svg>
          </div>
          <div className="user-info">
            <span className="user-name">{user?.name || "Admin"}</span>
            <span className="user-role">{user?.scope === "platform" ? "Platform" : `${user?.role || ""} · ${user?.vendorName || user?.vendorId || ""}`}</span>
          </div>
        </div>
        {pageContext && (
          <div className="topbar-page-context">
            <span>{pageContext.eyebrow}</span>
            <strong>{pageContext.title}</strong>
          </div>
        )}
      </div>

      <div className="topbar-page-actions" id="admin-topbar-actions" />

      <div className="topbar-right">
        <div className="date-display">{getCurrentDate()}</div>

        {showFollowups && <div className="notification-wrap" ref={panelRef}>
          <button type="button" className="notification-bell" onClick={() => setOpen((value) => !value)} aria-label="Lead follow-up notifications">
            <Bell className="bell-icon" size={19} />
            {followups.totalDue > 0 && <span className="notification-badge">{Math.min(followups.totalDue, 9)}</span>}
          </button>
          {open && (
            <div className="notification-panel">
              <div className="notification-panel-head">
                <strong>Lead follow-ups</strong>
                <span>{followups.totalDue || 0} due</span>
              </div>
              {(followups.items || []).length === 0 ? (
                <p>No due follow-ups.</p>
              ) : (
                (followups.items || []).map((item) => (
                  <button type="button" key={item._id} onClick={openLeads}>
                    <strong>{item.name || "Lead"}</strong>
                    <span>{item.status === "overdue" ? "Overdue" : "Due today"} · {item.priority || "medium"}</span>
                    <small>{item.requirement || item.phone || "Follow-up required"}</small>
                  </button>
                ))
              )}
              <button className="notification-view-all" type="button" onClick={openLeads}>View Leads</button>
            </div>
          )}
        </div>}

        <a className="dashboard-call-button" href="tel:7053477493">
          <Phone size={15} />
          <span>Call Desk</span>
        </a>

        {String(user?.userId || "") !== "env-superadmin" && (
          <button type="button" className="logout-button change-password-button" aria-label="Change password" onClick={() => window.location.assign(adminPath("/change-password"))}>
            Change password
          </button>
        )}
        <button type="button" className="logout-button" aria-label="Logout" onClick={handleLogout}>
          Logout
        </button>
      </div>
    </header>
  );
}
